import { PropsWithChildren, useCallback } from 'react';

import type { StudentResponse } from '@givebacks/api/student';
import { useShowConsumerStudent } from '@givebacks/api/student';
import { Navigate, Outlet, useMatch, useNavigate, useParams } from 'react-router-dom';
import { Box, SegmentedControl, Stack } from 'ui';
import { OneToTwoLayoutTemplate } from 'ui/Core';

import { StudentInfoTotalDueCard } from '../components/molecules/cards/StudentInfoTotalDueCard';

const RouteSegmentedControl = () => {
  const navigate = useNavigate();
  const isPaymentRoute = useMatch('student-fees/students/:studentId/payments/*');
  const { studentId } = useParams();

  const handleChange = useCallback(
    (value: string) => {
      if (value === 'payments') {
        navigate(`/student-fees/students/${studentId}/payments`);
      } else {
        navigate(`/student-fees/students/${studentId}/unpaid-fees`);
      }
    },
    [navigate, studentId]
  );
  return (
    <SegmentedControl
      variant="light"
      value={isPaymentRoute ? 'payments' : 'unpaid-fees'}
      onChange={handleChange}
      data={[
        { label: 'Unpaid', value: 'unpaid-fees' },
        { label: 'Paid', value: 'payments' },
      ]}
      size="sm"
    />
  );
};

/** Selects only the student uuid from the response to ensure the student is found */
const selectStudent = (data: StudentResponse) => {
  return data.student.uuid;
};
/** Returns a component that will a empty state when a student is not found */
const StudentGuard = ({ children, studentId }: PropsWithChildren<{ studentId: string }>) => {
  const { error } = useShowConsumerStudent({
    studentId: studentId,
    select: selectStudent,
  });

  if (error) {
    return <Navigate to="/student-fees/students" />;
  }
  return children;
};

type StudentFeesLayoutProps = PropsWithChildren;
const StudentsLayout = ({ children }: StudentFeesLayoutProps) => {
  const { studentId } = useParams();

  if (!studentId) {
    return <Navigate to="/student-fees/students" />;
  }
  return (
    <StudentGuard studentId={studentId}>
      <OneToTwoLayoutTemplate
        leftSection={<StudentInfoTotalDueCard studentId={studentId} />}
        rightSection={
          <Stack>
            <RouteSegmentedControl />
            {/* Set a relative box so that loading overlay is centered  on following elements*/}
            <Box mih={300} pos="relative">
              {children}
            </Box>
          </Stack>
        }
      />
    </StudentGuard>
  );
};

const Students = () => {
  return (
    <StudentsLayout>
      <Outlet />
    </StudentsLayout>
  );
};

export { Students };
